"use client";

import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Text,
  VStack,
  CloseButton,
  HStack,
  Icon,
  Box,
  Flex,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { supabase } from "../../supabaseClient";

function collectTemplateVariables(...texts: string[]): string[] {
  const regex = /{{\s*([^{}]+?)\s*}}/g;
  const variables = new Set<string>();
  for (const text of texts) {
    if (!text) continue;
    let match: RegExpExecArray | null;
    const local = new RegExp(regex.source, regex.flags);
    while ((match = local.exec(text)) !== null) {
      const variableName = (match[1] || "").trim();
      if (variableName) variables.add(variableName);
    }
  }
  return Array.from(variables);
}

interface SendInitialEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  fromEmail: string;
  initialEmailSubject?: string;
  initialEmailContent?: string;
}

export const SendInitialEmailDialog: React.FC<SendInitialEmailDialogProps> = ({
  isOpen,
  onClose,
  agentId,
  fromEmail,
  initialEmailSubject = "",
  initialEmailContent = "",
}) => {
  const [toEmail, setToEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});
  const toast = useToast();

  const modalContentBg = "white";
  const labelColor = "gray.700";
  const infoTextColor = "gray.600";

  const extractedVariables = useMemo(
    () =>
      collectTemplateVariables(initialEmailSubject, initialEmailContent),
    [initialEmailSubject, initialEmailContent]
  );

  useEffect(() => {
    if (!isOpen) {
      setToEmail("");
      setDynamicVariables({});
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!toEmail.trim()) {
      toast({
        title: "Email required",
        description: "Please enter a recipient email address",
        status: "error",
        duration: 3000,
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(toEmail)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsSending(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        throw new Error("Authentication required");
      }

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "user-id": session.user.id,
      };

      const serverUrl =
        process.env.REACT_APP_SERVER_URL || "http://localhost:8000";
      const response = await fetch(
        `${serverUrl}/api/email/send-initial-email`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            agent_id: agentId,
            to_email: toEmail,
            dynamic_vars_values: dynamicVariables,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send email");
      }

      toast({
        title: "Email sent successfully",
        description: `Initial email sent to ${toEmail}`,
        status: "success",
        duration: 5000,
      });

      setToEmail("");
      setDynamicVariables({});
      onClose();
    } catch (error) {
      console.error("Error sending email:", error);
      toast({
        title: "Error sending email",
        description:
          error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setToEmail("");
    setDynamicVariables({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalContentBg} maxW="500px">
        <ModalHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pb={4}
          color={labelColor}
        >
          Send Initial Email
          <CloseButton onClick={handleClose} />
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              Send an initial email from <strong>{fromEmail}</strong> to start a
              conversation.
            </Text>
            <FormControl isRequired>
              <FormLabel color={labelColor}>Recipient Email Address</FormLabel>
              <Input
                placeholder="example@email.com"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                type="email"
                autoFocus
                borderColor="gray.300"
              />
            </FormControl>

            {extractedVariables.length > 0 && (
              <Box
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                p={3}
              >
                <Text fontWeight="semibold" fontSize="sm" color={labelColor}>
                  Dynamic Variables
                </Text>
                <Text fontSize="xs" color={infoTextColor} mt={1} mb={3}>
                  Set dynamic variables for this initial email test (subject and
                  body)
                </Text>

                <Flex
                  mb={2}
                  fontWeight="medium"
                  fontSize="xs"
                  color={labelColor}
                >
                  <Box flex="1">Variable Name</Box>
                  <Box flex="1">Test Value</Box>
                </Flex>

                <VStack spacing={2} align="stretch">
                  {extractedVariables.map((variable) => (
                    <HStack key={variable} spacing={2}>
                      <Input
                        size="sm"
                        value={variable}
                        isReadOnly
                        fontSize="xs"
                        bg="white"
                      />
                      <Input
                        size="sm"
                        placeholder="Enter value"
                        value={dynamicVariables[variable] || ""}
                        fontSize="xs"
                        onChange={(e) =>
                          setDynamicVariables((prev) => ({
                            ...prev,
                            [variable]: e.target.value,
                          }))
                        }
                      />
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}

            <HStack spacing={2} color={infoTextColor} fontSize="sm">
              <Icon as={InfoIcon} />
              <Text>The agent will send its configured initial email.</Text>
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button
            onClick={handleClose}
            variant="ghost"
            colorScheme="white"
            textColor="gray.800"
            isDisabled={isSending}
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleSend}
            isLoading={isSending}
            loadingText="Sending..."
          >
            Send
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
